import React, { useState, useEffect } from 'react';
import { Button, message, Space, Spin, Input } from 'antd';
import axios from 'axios';
import { format } from 'date-fns';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import { useParams } from 'react-router-dom';

const OrgModels = () => {
  const [orgModels, setOrgModels] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentOrgModel, setCurrentOrgModel] = useState(null);
  const [initialFormData, setInitialFormData] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const { orgId } = useParams();

  useEffect(() => {
    fetchOrgModels();
    fetchModels();
  }, [pagination.current, searchTerm]);

  const fetchOrgModels = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/org-models/${orgId}/organization`,
        {
          params: {
            page: pagination.current,
            limit: pagination.pageSize,
            search: searchTerm,
          },
        }
      );

      const { data, meta } = response.data;
      const formattedOrgModels = data.map(orgModel => ({
        id: orgModel.id,
        organizationId: orgModel.organizationId,
        modelId: orgModel.modelId,
        maxQueue: orgModel.maxQueue,
        modelName: orgModel.model.name,
        inputCost: orgModel.inputCost * 1000000,
        outputCost: orgModel.outputCost * 1000000,
        rateLimitPerMinute: orgModel.rateLimitPerMinute,
        rateLimitPerToken: orgModel.rateLimitPerToken,
        status: orgModel.status,
        createdAt: format(new Date(orgModel.createdAt), 'MM/dd/yyyy, HH:mm:ss'),
        updatedAt: format(new Date(orgModel.updatedAt), 'MM/dd/yyyy, HH:mm:ss'),
      }));

      setOrgModels(formattedOrgModels);
      setPagination(prev => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error) {
      console.error('Error fetching OrgModels:', error);
      message.error('Failed to load OrgModels');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/models`);
      setModels(response.data.data);
    } catch (error) {
      console.error('Error fetching models:', error);
      message.error('Failed to load models');
    }
  };

  const openModal = (orgModel = null) => {
    setCurrentOrgModel(orgModel);
    setInitialFormData({
      modelId: orgModel?.modelId || '',
      maxQueue: orgModel?.maxQueue || '',
      inputCost: orgModel?.inputCost  || '',
      outputCost: orgModel?.outputCost  || '',
      rateLimitPerMinute: orgModel?.rateLimitPerMinute || '',
      rateLimitPerToken: orgModel?.rateLimitPerToken || '',
    });
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCurrentOrgModel(null);
    setInitialFormData({});
  };

  const handleCreateUpdateOrgModel = async formData => {
    try {
      const url = `${process.env.REACT_APP_API}/api/org-models`;
      const dataToSend = {
        organizationId: orgId,
        modelId: formData.modelId,
        maxQueue: formData.maxQueue,
        inputCost: formData.inputCost / 1000000,
        outputCost: formData.outputCost / 1000000,
        rateLimitPerMinute: formData.rateLimitPerMinute,
        rateLimitPerToken: formData.rateLimitPerToken,
      };

      if (currentOrgModel) {
        // Update OrgModel
        await axios.patch(`${url}/${currentOrgModel.id}`, dataToSend);
        message.success('OrgModel updated successfully');
      } else {
        // Create OrgModel
        await axios.post(url, dataToSend);
        message.success('OrgModel created successfully');
      }

      fetchOrgModels();
      closeModal();
    } catch (error) {
      console.error('Error creating/updating OrgModel:', error);
      message.error(error?.response?.data?.message || 'An error occurred');
    }
  };

  const handleToggleOrgModelStatus = async orgModel => {
    try {
      await axios.patch(`${process.env.REACT_APP_API}/api/org-models/${orgModel.id}/status`, {});
      message.success('OrgModel status updated successfully');
      fetchOrgModels();
    } catch (error) {
      console.error('Error updating OrgModel status:', error);
      message.error('Failed to update OrgModel status');
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page on search
  };

  const handleTableChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div>
      <div>
        <Space style={{ display: 'flex', justifyContent: 'end', marginBottom: '16px' }}>
        
          <Button type="primary" onClick={() => openModal()}>
            Create OrgModel
          </Button>
        </Space>
        <GenericTable
          data={orgModels}
          searchKeys={['organizationId', 'modelId', 'status']}
          sortableColumns={['modelName', 'status', 'inputCost', 'outputCost']}
          actions={['Edit', 'Toggle Status']}
          actionFunctions={[
            openModal,
            (orgModel) => handleToggleOrgModelStatus(orgModel),
          ]}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handleTableChange,
          }}
          searchInput={searchTerm}
          onSearch={handleSearch}
          toggle="status"
        />
        <GenericModal
          isVisible={isModalVisible}
          handleOk={handleCreateUpdateOrgModel}
          handleCancel={closeModal}
          title={currentOrgModel ? 'Edit OrgModel' : 'Create OrgModel'}
          inputs={[
            {
              label: 'Model',
              name: 'modelId',
              type: 'select',
              options: models?.map(model => ({ value: model.id, label: model.name })),
              placeholder: 'Select a Model',
              require: true,
            },
            {
              label: 'Max Queue',
              name: 'maxQueue',
              type: 'number',
              placeholder: 'Enter Max Queue',
              require: true,
            },
            {
              label: 'Input Cost',
              name: 'inputCost',
              type: 'number',
              placeholder: 'Enter Input Cost',
              require: true,
            },
            {
              label: 'Output Cost',
              name: 'outputCost',
              type: 'number',
              placeholder: 'Enter Output Cost',
              require: true,
            },
            {
              label: 'Rate Limit Per Minute',
              name: 'rateLimitPerMinute',
              type: 'number',
              placeholder: 'Enter Rate Limit Per Minute',
              require: true,
            },
            {
              label: 'Rate Limit Per Token',
              name: 'rateLimitPerToken',
              type: 'number',
              placeholder: 'Enter Rate Limit Per Token',
              require: true,
            },
          ]}
          actions={['Submit', 'Cancel']}
          actionFunctions={[handleCreateUpdateOrgModel, closeModal]}
          predefinedData={initialFormData}
        />
      </div>
    </div>
  );
};

export default OrgModels;
